import React, { useEffect, useMemo, useState } from "react";
import {
	Accordion, AccordionBody, AccordionHeader, AccordionItem, Card, CardBody,
	Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import Co2UniversalButton from "Components/Co2Common/Co2UniversalButton";
import Co2ButtonInput from "Components/Co2Common/Co2ButtonInput";
import Co2Toggle from "Components/Co2Common/Fields/Co2Toggle";
import LicensePlanSelector from "Components/Co2Common/LicensePlanSelector";
import NumberInput from "Components/Co2Common/NumberInput";
import LicenseCost from "pages/Co2Auth/Components/LicenseCost";
import { getLicenseRenewalDetails, getRenewalConfig, toggleAutoRenewal } from "helpers/backend_helper";

interface Props {
	open: boolean;
	licenseId: number | string;
	onClose: () => void;
	onSaved: () => void;
}

const pounds = (value: number) => `£${Number(value || 0).toLocaleString("en-GB", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
})}`;
const date = (value?: string) => value ? new Date(value).toLocaleString("en-GB") : "—";
const periodLabel = (months: number) => months >= 12 ? "per year" : months === 1 ? "per month" : `per ${months} months`;

const RenewalDetailsModal = ({ open, licenseId, onClose, onSaved }: Props) => {
	const [details, setDetails] = useState<any>(null);
	const [autoRenew, setAutoRenew] = useState(true);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [availablePlans, setAvailablePlans] = useState<any[]>([]);

	useEffect(() => {
		if (!open) return;
		setLoading(true);
		Promise.all([
			getLicenseRenewalDetails(licenseId),
			getRenewalConfig({ pricing_mode: "scheduled" }).catch(() => null),
		])
			.then(([response, config]: any[]) => {
				setDetails(response);
				setAutoRenew(!!response?.autoRenew);
				setAvailablePlans([
					...(config?.plans?.monthly || []),
					...(config?.plans?.annual || []),
				]);
			})
			.catch((error: any) => toast.error(error?.response?.data?.error || "Failed to load renewal details."))
			.finally(() => setLoading(false));
	}, [open, licenseId]);

	const additionalUsers = Math.max(0, Number(details?.allowedUsers || 0) - Number(details?.includedUsers || 0));
	const additionalSites = Math.max(0, Number(details?.allowedSites || 0) - Number(details?.includedSites || 0));
	const planPrice = Number(details?.periodSubtotal || 0) / Math.max(1, Number(details?.totalBillingCycles || 1));
	const displayedPlans = useMemo(() => {
		if (!details) return [];
		const matching = availablePlans.filter((plan: any) =>
			Number(plan.planMonths) === Number(details.planMonths) &&
			Number(plan.billingCycleMonths) === Number(details.billingCycleMonths),
		);
		if (matching.length > 0) return matching;
		return [{
			id: details.plan?.id,
			code: details.plan?.code,
			name: details.plan?.name,
			includedUsers: details.includedUsers,
			includedSites: details.includedSites,
			baseCost: planPrice,
		}];
	}, [availablePlans, details, planPrice]);
	const paymentCycleOptions = useMemo(() => {
		if (!details) return [];
		const cycles = Array.from(new Set(
			availablePlans
				.filter((plan: any) => Number(plan.planMonths) === Number(details.planMonths))
				.map((plan: any) => Number(plan.billingCycleMonths))
				.filter((months: number) => months > 0),
		)).sort((a, b) => a - b);
		return cycles.length > 0 ? cycles : [Number(details.billingCycleMonths)];
	}, [availablePlans, details]);
	const licenseSettings = useMemo(() => details ? {
		costSummary: details.costSummary,
		totalExclVat: details.periodSubtotal,
		grossLicenseCosstExclVat:
			Number(details.costSummary?.base?.gross || 0) +
			Number(details.costSummary?.users?.gross || 0) +
			Number(details.costSummary?.sites?.gross || 0),
		discountAmount: 0,
		vatAmount: details.periodVat,
		totalCost: details.periodTotal,
		vatPercentage: details.costSummary?.vat?.pct,
		billingCycle: details.totalBillingCycles > 1 ? "monthly" : "annual",
	} : null, [details]);

	const save = async () => {
		setSaving(true);
		try {
			await toggleAutoRenewal({ enable: autoRenew });
			onSaved();
		} catch (error: any) {
			toast.error(error?.response?.data?.error || "Failed to update auto-renewal.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal isOpen={open} toggle={onClose} centered size="lg" backdrop="static" className="subscription-change-modal">
			<ModalHeader toggle={onClose} className="border-bottom pb-3">
				<div className="d-flex align-items-center">
					<i className="fa fa-light fa-credit-card fs-20 text-primary me-2"></i>
					<span className="fs-16 fw-semibold">Renewal details</span>
				</div>
			</ModalHeader>

			<ModalBody className="p-4">
				{loading && <div className="text-center py-5"><Spinner /></div>}
				{!loading && details && (
					<>
						<div className="mb-3">
							<Row className="g-2 align-items-end">
								<Col lg={2} md={4} sm={6}><Label className="form-label mb-1">Status</Label><Input readOnly value="Renewal preview" /></Col>
								<Col lg={3} md={3} sm={6}><Label className="form-label mb-1">Start date</Label><Input readOnly value={date(details.period?.start)} /></Col>
								<Col lg={3} md={3} sm={6}><Label className="form-label mb-1">Expiry date</Label><Input readOnly value={date(details.period?.end)} /></Col>
								<Col lg={2} md={2} sm={6}><Label className="form-label mb-1">Total license cost</Label><Input readOnly className="text-end fw-bold text-primary" value={pounds(details.periodTotal)} /></Col>
								<Col lg={2} md={4} sm={6}>
									<Co2Toggle id="renewal-auto-renew" label="Auto renewal" checked={autoRenew} onChange={setAutoRenew} onText="On" offText="Off" />
								</Col>
							</Row>
						</div>

						<Accordion className="custom-accordionwithicon custom-accordion-border accordion-border-box accordion-success" open="renewal-plan" toggle={() => null}>
							<AccordionItem className="material-shadow" style={{ flexShrink: 0 }}>
								<AccordionHeader targetId="renewal-plan">Plan details</AccordionHeader>
								<AccordionBody accordionId="renewal-plan" className="text-body">
									<LicensePlanSelector
										contractOptions={[{ key: String(details.planMonths), label: `${details.planMonths} month contract` }]}
										selectedContractOption={String(details.planMonths)}
										paymentCycleOptions={paymentCycleOptions.map((months) => ({
											key: String(months),
											label: months >= 12 ? "Annual" : months === 1 ? "Monthly" : `Every ${months} months`,
										}))}
										selectedPaymentCycleOption={String(details.billingCycleMonths)}
										onSelectPaymentCycleOption={() => null}
										plans={displayedPlans.map((plan: any) => {
											const current = Number(plan.id) === Number(details.plan?.id) ||
												(String(plan.code || "").toLowerCase() === String(details.plan?.code || "").toLowerCase());
											return {
												key: String(plan.id || plan.code || plan.name),
												title: plan.name,
												price: Number(plan.baseCost || 0).toFixed(2),
												period: periodLabel(details.billingCycleMonths),
												features: [`${plan.includedUsers || 1} users`, `${plan.includedSites || 1} sites`],
												highlight: current,
												ctaText: current ? "Current" : "Select",
											};
										})}
										onSelectPlan={() => null}
										readOnly
										voucherContent={details.voucher?.code ? (
											<div>
												<Label htmlFor="renewal-voucher" className={`form-label mb-1 ${details.voucher.applied ? "text-body" : "text-danger"}`}>
													<span className="fw-semibold">Voucher code</span>
												</Label>
												<Co2ButtonInput
													name="renewal-voucher" placeholder="" value={details.voucher.code}
													invalid={!details.voucher.applied} error="Voucher will not be applied"
													loader={false} readOnly hideButton required={false} showHelp={false}
													handleSearch={() => null} handleBlur={() => null} handleChange={() => null}
												/>
												<div className={`${details.voucher.applied ? "text-success" : "text-danger"} small mt-2`}>
													{details.voucher.applied ? "Discount will be applied to the renewal." : details.voucher.reason}
												</div>
											</div>
										) : undefined}
										plansRowClassName="g-3 mb-2"
									/>

									<div className="mb-1">
										<Row className="align-items-center mb-1"><Col xl={3} md={3}></Col><Col xl={6} md={6}><div className="d-grid w-100 fw-semibold" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr", columnGap: 12 }}><div>Included</div><div>Additional</div><div>In Use</div><div>Total</div></div></Col></Row>
										{[
											["Number of users", details.includedUsers, additionalUsers, details.actualUsers, details.allowedUsers],
											["Number of operational sites", details.includedSites, additionalSites, details.actualSites, details.allowedSites],
										].map(([label, included, additional, actual, total]) => (
											<Row className="mb-1 align-items-center" key={String(label)}>
												<Col xl={3} md={3}><Label className="form-label mb-0 text-body"><span className="fw-semibold">{label}</span></Label></Col>
												<Col xl={6} md={6}><div className="d-grid w-100 align-items-center" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr", columnGap: 12 }}>
													{[included, additional, actual, total].map((value, index) => <NumberInput key={index} value={Number(value)} onChange={() => null} min={0} max={9999} readOnly />)}
												</div></Col>
											</Row>
										))}
									</div>
								</AccordionBody>
							</AccordionItem>
						</Accordion>

						<div className="mt-2">
							<Card className="profile-project-card shadow-none material-shadow profile-project-primary mb-2"><CardBody className="py-3"><Row className="px-2">
								<LicenseCost licenseSettings={licenseSettings} summaryHeaderLabel="Renewal license costs" totalLabel="New license cost" totalRowClassName="border-top" baseCostLabel={`${details.plan?.name || "License"} license cost`} additionalUsersCount={additionalUsers} additionalSitesCount={additionalSites} showBaseRow noCard />
								<table className="w-100 mt-1"><tbody>
									<tr><td className="fw-bold">First renewal payment</td><td className="fw-bold text-end text-primary">{pounds(details.firstPayment?.total)}</td></tr>
									{details.firstPayment?.balanceApplied > 0 && <tr><td className="text-muted">Using available balance</td><td className="text-end text-danger">-{pounds(details.firstPayment.balanceApplied)}</td></tr>}
									<tr><td className="fw-bold">Card payment on renewal</td><td className="fw-bold text-end text-primary">{pounds(details.firstPayment?.cardAmount)}</td></tr>
								</tbody></table>
							</Row></CardBody></Card>
						</div>

					</>
				)}
			</ModalBody>

			<ModalFooter className="border-top py-3 justify-content-between">
				<Co2UniversalButton onClick={onClose} iconClass="fa fa-light fa-xmark" disabled={saving}>Cancel</Co2UniversalButton>
				<Co2UniversalButton onClick={save} variant="primary" iconPosition="right" iconClass="fa fa-light fa-circle-check" disabled={loading || saving || !details || autoRenew === !!details.autoRenew} loading={saving}>{saving ? "Processing..." : "Save changes"}</Co2UniversalButton>
			</ModalFooter>
		</Modal>
	);
};

export default RenewalDetailsModal;
